import { request } from './client'

/**
 * The customer-facing service catalogue and the Sandbox.
 *
 * Three endpoints, and one rule that shapes all of them: the browser never
 * talks to an upstream. The Sandbox posts to `sandbox/execute/` and the server
 * runs the same gateway pipeline a machine call runs — permission, idempotency,
 * wallet hold, routing, capture. There is no shortcut from here to anybody's
 * provider, and adding one would move billing and access control into a place
 * a customer can edit.
 *
 * Nothing in this module knows which capabilities exist. The list endpoint
 * returns exactly the granted, in-scope services for one customer, and a
 * capability that is dormant is simply absent — so the portal cannot show one
 * by holding a stale list of its own.
 */

export type FieldType = 'string' | 'integer' | 'boolean' | 'number' | 'list' | 'object'

/** One field of a capability's public contract, as the server describes it. */
export interface Field {
  name: string
  type: FieldType
  required: boolean
  nullable: boolean
  /** Present for `list` and `object`: the shape of what is inside. */
  children?: Field[]
}

/** An application of this customer that holds a grant for the service. */
export interface ServiceApplication {
  id: string
  name: string
}

export interface Service {
  code: string
  name: string
  display_name_fa: string
  description: string
  /** banking | cheque | identity_person | identity_mobile */
  domain: string
  // Null while nobody has priced the operation. Promising a number here is
  // what let a sibling console crash on a fresh platform.
  sale_price_irr: number | null
  applications: ServiceApplication[]
  inputs: string[]
  result_fields: string[]
}

export interface ServiceDetail extends Service {
  contract: { request: Field[]; result: Field[] }
}

export interface SandboxBody {
  application: string
  operation: string
  payload: Record<string, unknown>
  idempotency_key?: string
}

/**
 * What one Sandbox run produced.
 *
 * `error` being set is a 200. A business rejection is an *answer* — the service
 * understood the question and declined it — and rendering it as a crashed page
 * would teach a customer to treat a legitimate outcome as a platform fault.
 */
export interface SandboxOutcome {
  request_id: string
  correlation_id: string | null
  replayed: boolean
  status: string
  duration_ms: number | null
  charged_amount_irr: number
  sale_price_irr: number
  result: Record<string, unknown> | null
  error: { code: string; message: string } | null
}

const scope = (customerId: string) => `/customers/${customerId}`

export const listServices = (customerId: string, signal?: AbortSignal) =>
  request<{ services: Service[] }>(`${scope(customerId)}/services/`, signal ? { signal } : {})

export const getService = (customerId: string, code: string, signal?: AbortSignal) =>
  request<ServiceDetail>(
    `${scope(customerId)}/services/${encodeURIComponent(code)}/`,
    signal ? { signal } : {},
  )

/**
 * A correlation id for one Sandbox run.
 *
 * The Sandbox shows the customer both ids side by side, so it has to send one
 * the way their own code would — `X-Request-ID` on the call — rather than have
 * the server invent one. The character set is the one the server accepts.
 */
const correlationId = () =>
  `sandbox-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

export const runSandbox = (customerId: string, body: SandboxBody) =>
  request<SandboxOutcome>(`${scope(customerId)}/sandbox/execute/`, {
    method: 'POST',
    body,
    correlationId: correlationId(),
  })
